import { useMutation, useQuery } from '@apollo/client'
import { Circle } from '@mui/icons-material'
import {
    Checkbox,
    ListItemText,
    MenuItem,
    Select,
    type SelectChangeEvent,
} from '@mui/material'
import {
    useGridApiContext,
    type GridRenderEditCellParams,
} from '@mui/x-data-grid-pro'
import { type GetRosterColorsQuery } from '__generated__/graphql'
import { useState, type FC } from 'react'
import {
    GetRosterColors,
    UpdateRosterCandidateColors,
} from 'recruiter/graphql/rosters.gql'
import useToast from 'shared/components/Toast/useToast'
import { formatGraphQLError, getPxAsRem } from 'shared/utils'
import ColorsAvatarGroup from './ColorsAvatarGroup'

interface ColorsDropdownProps extends GridRenderEditCellParams {
    colors: GetRosterColorsQuery['rosterColors']
    rosterCandidateId: string
    /** Left for API compatibility but no longer used to force a refetch */
    refetchQueries?: any
    canEdit?: boolean
}

const ColorsDropdown: FC<ColorsDropdownProps> = ({
    colors,
    rosterCandidateId,
    canEdit = false,
    ...params
}) => {
    const { data: colorOptions } = useQuery(GetRosterColors)
    const [selectedColors, setSelectedColors] = useState(
        colors?.map((c) => c?.id) || []
    )
    const gridApi = useGridApiContext()
    const toast = useToast()

    const colorChoices = colorOptions?.rosterColors
        ? [...colorOptions.rosterColors].sort((a, b) => a.order - b.order)
        : []

    const handleChange = (event: SelectChangeEvent<string[]>) => {
        if (typeof event.target.value !== 'string')
            setSelectedColors(event.target.value)
    }

    const [updateCandidateColors] = useMutation(UpdateRosterCandidateColors, {
        onCompleted: () => {
            toast({
                title: 'Success!',
                message: 'Candidate color(s) updated',
                severity: 'success',
            })

            /* 🔸 patch only this row in the grid */
            const rowId = params?.id?.toString()
            if (rowId && gridApi?.current?.getRow) {
                const existingRow: any = gridApi.current.getRow(rowId)
                if (existingRow) {
                    const updatedRow = {
                        ...existingRow,
                        rosterCandidate: {
                            ...existingRow.rosterCandidate,
                            candidateColors: selectedColors.map((colorId) => ({
                                rosterColor:
                                    colorChoices.find(
                                        (c) => c.id === colorId
                                    ) ?? null,
                            })),
                        },
                    }
                    gridApi.current.updateRows([updatedRow])
                }
            }
        },
        onError: (error) => {
            toast({
                title: 'Error!',
                message: formatGraphQLError(
                    error,
                    'Failed to save candidate color(s)'
                ),
                severity: 'error',
            })
        },
    })

    return (
        <Select
            disabled={!canEdit}
            onClose={() => {
                const existingColorIds = colors.map((c) => c.id)
                const colorsHaveChanged =
                    selectedColors.length !== existingColorIds.length ||
                    !selectedColors.every((id) => existingColorIds.includes(id))

                if (canEdit && colorsHaveChanged) {
                    updateCandidateColors({
                        variables: {
                            rosterCandidateIds: [rosterCandidateId],
                            rosterColorIds: selectedColors,
                        },
                    })
                }

                gridApi.current.stopCellEditMode({
                    id: params?.id,
                    field: params?.field,
                })
            }}
            sx={{
                padding: 0,
                width: '100%',
                alignItems: 'flex-start',
                boxShadow: 'none',
                '.MuiOutlinedInput-notchedOutline': { border: 0 },
            }}
            label="select colors for row"
            inputProps={{
                sx: { width: '100%', height: '100%', padding: getPxAsRem(10) },
            }}
            multiple
            open
            value={selectedColors}
            onChange={handleChange}
            renderValue={() => (
                <ColorsAvatarGroup
                    id={params?.id?.toString()}
                    colors={colorChoices.filter((c) =>
                        selectedColors.includes(c?.id)
                    )}
                />
            )}
        >
            {colorChoices.map((color) => (
                <MenuItem key={color.id} value={color.id} dense>
                    <Checkbox checked={selectedColors.includes(color.id)} />
                    <ListItemText primary={color.value} />
                    <Circle
                        sx={{ fill: color.hexCode, marginLeft: getPxAsRem(12) }}
                    />
                </MenuItem>
            ))}
        </Select>
    )
}

export default ColorsDropdown
